import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { fmtDate, fmtNum } from "../lib/format";
import { canOperationalApprove, hasRole, useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { roomTypeFor, linenForType, matchItem } from "../lib/roomLinen";
import { useLiveRefresh } from "../hooks/useLiveRefresh";

const REASONS = [
  { value: "housekeeper_error", label: "Housekeeper entry error" },
  { value: "item_in_room", label: "Item still in room" },
  { value: "collected_separately", label: "Item collected separately" },
  { value: "damaged", label: "Damaged item" },
  { value: "possible_loss", label: "Unable to locate / possible loss" },
  { value: "other", label: "Other — remarks required" },
];
const reasonLabel = (v) =>
  REASONS.find((r) => r.value === v)?.label || v || "—";

const STATUS_META = {
  open: { label: "New", cls: "bg-slate-100 text-slate-700 border" },
  counted: {
    label: "Awaiting Verification",
    cls: "bg-amber-100 text-amber-800 border",
  },
  awaiting_verification: {
    label: "Awaiting Verification",
    cls: "bg-amber-100 text-amber-800 border",
  },
  discrepancy: { label: "Discrepancy", cls: "bg-red-100 text-red-800 border" },
  verified: { label: "Verified", cls: "bg-blue-100 text-blue-800 border" },
  escalated: { label: "Escalated", cls: "bg-red-100 text-red-800 border" },
  collected: {
    label: "Collected to Dirty Holding",
    cls: "bg-emerald-100 text-emerald-800 border",
  },
};

export default function Checkout() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canSubmit = hasRole(user, "admin", "accounts", "hk_supervisor", "hk_staff", "front_office");
  const canVerify = canOperationalApprove(user) || hasRole(user, "front_office");
  const canCancel = hasRole(user, "admin");

  const [tasks, setTasks] = useState([]);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [countTask, setCountTask] = useState(null);
  const [verifyTask, setVerifyTask] = useState(null);
  const [collectTask, setCollectTask] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [cancelTask, setCancelTask] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [counts, setCounts] = useState({});
  const [roomBal, setRoomBal] = useState({});
  const [vrows, setVrows] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [tk, i, r] = await Promise.all([
      api.get("/checkout-tasks"),
      api.get("/items"),
      api.get("/rooms"),
    ]);
    setTasks(tk.data);
    setItems(i.data);
    setRooms(r.data);
  }, []);

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  useLiveRefresh(load);

  const itemName = (id) => items.find((x) => x.id === id)?.name || id;
  const roomInfo = useMemo(() => {
    const m = {};
    rooms.forEach((r) => {
      m[r.id] = {
        number: r.number,
        type: r.room_type || roomTypeFor(r.number) || "Setup Pending",
      };
    });
    return m;
  }, [rooms]);
  const roomLabel = (rid) => {
    const r = roomInfo[rid];
    return r ? `Room ${r.number} — ${r.type}` : "—";
  };
  const st = (task) => STATUS_META[task.status || "open"] || STATUS_META.open;

  // ---- Housekeeper count submission ----
  const templateItemsFor = (task) => {
    const type = roomInfo[task.room_id]?.type;
    const labels = type && type !== "Setup Pending" ? linenForType(type) : [];
    const matched = labels.map((l) => matchItem(l, items)).filter(Boolean);
    const list = matched.length
      ? matched
      : items.filter((it) => it.active !== false);
    return list.filter((it) => it.launderable !== false); // Extra Bed excluded (non-launderable)
  };
  const openCount = async (task) => {
    const init = {};
    (task.hk_lines || []).forEach((l) => {
      init[l.item_id] = l.qty;
    });
    setCounts(init);
    setRemarks(task.hk_remarks || "");
    setCountTask(task);
    setRoomBal({});
    try {
      const { data } = await api.get(`/rooms/${task.room_id}/linen-balance`);
      setRoomBal(data || {});
    } catch (e) {
      setRoomBal({});
    }
  };
  const submitCount = async () => {
    // C4: revalidate against the LATEST room balance before submitting.
    let latest = roomBal;
    try {
      const { data } = await api.get(
        `/rooms/${countTask.room_id}/linen-balance`,
      );
      latest = data || {};
      setRoomBal(latest);
    } catch (e) {
      console.warn("Room balance re-fetch failed; using cached balance", e);
    }
    const entries = Object.entries(counts).filter(
      ([, q]) => String(q).trim() !== "" && Number(q) > 0,
    );
    if (entries.length === 0) {
      toast.error("Enter at least one dirty linen quantity");
      return;
    }
    const bad = entries.filter(
      ([id, q]) => !/^\d+$/.test(String(q)) || Number(q) > (latest[id] || 0),
    );
    if (bad.length) {
      const names = bad
        .map(([id]) => `${itemName(id)} (room has ${latest[id] || 0})`)
        .join("; ");
      toast.error(
        `Dirty quantity must be a whole number not exceeding the current room balance: ${names}`,
      );
      return;
    }
    const lines = entries.map(([item_id, qty]) => ({
      item_id,
      qty: Number(qty),
    }));
    setBusy(true);
    try {
      await api.post("/checkout-tasks/count", {
        task_id: countTask.id,
        lines,
        remarks,
      });
      toast.success("Checkout submitted for verification");
      setCountTask(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---- Supervisor verify ----
  const openVerify = (task) => {
    const src = task.verify_lines?.length
      ? task.verify_lines
      : ((task.hk_lines?.length ? task.hk_lines : task.count_lines) || []).map(
          (l) => ({
            item_id: l.item_id,
            reported_qty: l.qty,
            actual_qty: l.qty,
            reason: "",
            remarks: "",
          }),
        );
    setVrows(
      src.map((l) => ({
        item_id: l.item_id,
        reported: l.reported_qty ?? l.reported ?? 0,
        actual: String(l.actual_qty ?? ""),
        reason: l.reason || "",
        remarks: l.remarks || "",
      })),
    );
    setRemarks(task.verify_remarks || "");
    setVerifyTask(task);
  };
  const updV = (id, k, v) =>
    setVrows((rows) =>
      rows.map((r) => (r.item_id === id ? { ...r, [k]: v } : r)),
    );
  const variance = (r) =>
    (r.actual === "" ? 0 : Number(r.actual)) - Number(r.reported);
  const vInvalid = vrows.some((r) => {
    if (r.actual !== "" && !/^\d+$/.test(String(r.actual))) return true;
    const va = variance(r);
    if (va !== 0 && !r.reason) return true;
    if (r.reason === "other" && !r.remarks.trim()) return true;
    return false;
  });
  const submitVerify = async () => {
    if (vInvalid) {
      toast.error(
        "Enter whole numbers and a reason (with remarks for 'Other') for every non-zero variance",
      );
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout-tasks/verify", {
        task_id: verifyTask.id,
        remarks,
        lines: vrows.map((r) => ({
          item_id: r.item_id,
          actual_qty: r.actual === "" ? 0 : Number(r.actual),
          reason: r.reason || null,
          remarks: r.remarks || null,
        })),
      });
      toast.success(
        data.status === "discrepancy" ? "Saved with discrepancy" : "Verified",
      );
      setVerifyTask(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---- Collect ----
  const doCollect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/checkout-tasks/${collectTask.id}/collect`,
      );
      toast.success(
        `Moved ${data.moved_qty ?? data.moved_items} unit(s) to Dirty Holding`,
      );
      setCollectTask(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---- Cancel (admin only) ----
  const doCancel = async () => {
    if (!cancelTask) return;
    if (!cancelReason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/checkout-tasks/${cancelTask.id}/cancel`, {
        reason: cancelReason.trim(),
      });
      toast.success(`Task cancelled (room ${cancelTask.room_number || cancelTask.room_id})`);
      setCancelTask(null);
      setCancelReason("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <h1 className="section-title" data-testid="page-title">
        {t("nav.checkout")}
      </h1>
      <div className="section-sub mb-4">{t("sub.checkout")}</div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="vr-table" data-testid="checkout-tasks-table">
            <thead>
              <tr>
                <th>Triggered</th>
                <th>Room</th>
                <th>Room Type</th>
                <th>Housekeeper</th>
                <th className="num">Reported Qty</th>
                <th>Status</th>
                <th>Verified by</th>
                <th>Collected by</th>
                <th className="no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => {
                const status = task.status || "open";
                const info = roomInfo[task.room_id] || {};
                return (
                  <tr
                    key={task.id}
                    data-testid={`checkout-row-${info.number || task.id}`}
                  >
                    <td>{fmtDate(task.triggered_at)}</td>
                    <td data-testid={`checkout-room-${task.id}`}>
                      Room {info.number || "—"}
                    </td>
                    <td>{info.type || "—"}</td>
                    <td>{task.hk_by_name || "—"}</td>
                    <td className="num">
                      {task.hk_total_qty != null
                        ? fmtNum(task.hk_total_qty)
                        : "—"}
                    </td>
                    <td>
                      <Badge className={st(task).cls}>{st(task).label}</Badge>
                    </td>
                    <td>{task.verified_by_name || "—"}</td>
                    <td>{task.collected_by_name || "—"}</td>
                    <td className="no-print flex flex-wrap gap-2">
                      {status === "collected" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDetailTask(task)}
                          data-testid={`details-btn-${task.id}`}
                        >
                          View Details
                        </Button>
                      ) : (
                        <>
                          {canSubmit &&
                            [
                              "open",
                              "awaiting_verification",
                              "counted",
                            ].includes(status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openCount(task)}
                                data-testid={`count-btn-${task.id}`}
                              >
                                Submit Count
                              </Button>
                            )}
                          {canVerify &&
                            [
                              "awaiting_verification",
                              "escalated",
                              "counted",
                            ].includes(status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openVerify(task)}
                                data-testid={`verify-btn-${task.id}`}
                              >
                                Count / Verify
                              </Button>
                            )}
                          {canVerify &&
                            ["verified", "discrepancy"].includes(status) && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openVerify(task)}
                                  data-testid={`reverify-btn-${task.id}`}
                                >
                                  Re-verify
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-slate-900 text-white hover:bg-slate-800"
                                  onClick={() => setCollectTask(task)}
                                  data-testid={`collect-btn-${task.id}`}
                                >
                                  Collect → Dirty Holding
                                </Button>
                              </>
                            )}
                          {canCancel &&
                            ["open", "counted", "awaiting_verification",
                             "verified", "discrepancy", "escalated"].includes(status) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-700 hover:bg-red-50"
                                onClick={() => setCancelTask(task)}
                                data-testid={`cancel-btn-${task.id}`}
                              >
                                Cancel task
                              </Button>
                            )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {tasks.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-slate-500 py-6">
                    No checkout tasks yet. Set a room to "Checked Out/Dirty" to
                    create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Housekeeper count dialog */}
      <Dialog open={!!countTask} onOpenChange={() => setCountTask(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Submit dirty linen · {countTask && roomLabel(countTask.room_id)}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 -mt-2 mb-1">
            Enter the dirty quantity per item. "In Room" is the current recorded
            room balance — you cannot report more than the room holds. Rows with
            none in the room are disabled.
          </div>
          <div className="space-y-2">
            {countTask &&
              templateItemsFor(countTask).map((it) => {
                const bal = roomBal[it.id] || 0;
                const disabled = bal <= 0;
                const val = counts[it.id] ?? "";
                const invalid =
                  String(val).trim() !== "" &&
                  (!/^\d+$/.test(String(val)) || Number(val) > bal);
                return (
                  <div
                    key={it.id}
                    className="grid grid-cols-12 gap-2 items-center border-b border-slate-100 py-1.5"
                  >
                    <div className="col-span-6 text-sm text-slate-800">
                      {it.name}
                      {disabled && (
                        <span className="text-xs text-amber-600 ml-1">
                          · none in room
                        </span>
                      )}
                    </div>
                    <div
                      className="col-span-3 text-xs text-slate-500 text-right"
                      data-testid={`count-inroom-${it.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      In room: <b className="text-slate-700">{fmtNum(bal)}</b>
                    </div>
                    <div className="col-span-3">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        max={bal}
                        placeholder="0"
                        disabled={disabled}
                        value={val}
                        onChange={(e) =>
                          setCounts({ ...counts, [it.id]: e.target.value })
                        }
                        className={`h-10 text-right ${invalid ? "border-red-400" : ""}`}
                        data-testid={`count-qty-${it.name.replace(/\s+/g, "-").toLowerCase()}`}
                      />
                    </div>
                  </div>
                );
              })}
            <div>
              <Label>Remarks</Label>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                data-testid="count-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCountTask(null)}>
              Cancel
            </Button>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800"
              onClick={submitCount}
              disabled={busy}
              data-testid="count-submit"
            >
              Submit Count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supervisor verify dialog */}
      <Dialog open={!!verifyTask} onOpenChange={() => setVerifyTask(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Verify collected linen ·{" "}
              {verifyTask && roomLabel(verifyTask.room_id)}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 mb-2">
            Reported by {verifyTask?.hk_by_name || "—"}. Enter the actual
            physically collected quantity; give a reason for any variance.
          </div>
          <div className="overflow-x-auto">
            <table className="vr-table">
              <thead>
                <tr>
                  <th>Linen Item</th>
                  <th className="num">Reported</th>
                  <th className="num">Actual</th>
                  <th className="num">Variance</th>
                  <th>Reason</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {vrows.map((r, i) => {
                  const va = variance(r);
                  return (
                    <tr key={r.item_id}>
                      <td>{itemName(r.item_id)}</td>
                      <td className="num">{fmtNum(r.reported)}</td>
                      <td className="num">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={r.actual}
                          onChange={(e) =>
                            updV(r.item_id, "actual", e.target.value)
                          }
                          className="h-9 w-20 ml-auto text-right"
                          data-testid={`verify-actual-${i}`}
                        />
                      </td>
                      <td
                        className={`num ${va < 0 ? "text-red-600" : va > 0 ? "text-blue-600" : "text-slate-500"}`}
                      >
                        {va > 0 ? `+${va}` : va}
                      </td>
                      <td>
                        <Select
                          value={r.reason}
                          onValueChange={(v) => updV(r.item_id, "reason", v)}
                          disabled={va === 0}
                        >
                          <SelectTrigger
                            className="h-9 w-52"
                            data-testid={`verify-reason-${i}`}
                          >
                            <SelectValue
                              placeholder={va === 0 ? "—" : "Select reason"}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {REASONS.map((x) => (
                              <SelectItem key={x.value} value={x.value}>
                                {x.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <Input
                          value={r.remarks}
                          onChange={(e) =>
                            updV(r.item_id, "remarks", e.target.value)
                          }
                          className="h-9"
                          data-testid={`verify-remark-${i}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Variance = Actual − Reported. Negative = Shortage, Positive =
            Excess. The housekeeper's numbers are never overwritten.
          </div>
          <div className="mt-2">
            <Label>Overall Remarks</Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              data-testid="verify-remarks"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyTask(null)}>
              Cancel
            </Button>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800"
              onClick={submitVerify}
              disabled={busy || vInvalid}
              data-testid="verify-submit"
            >
              Save Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collect confirmation */}
      <Dialog open={!!collectTask} onOpenChange={() => setCollectTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Collect to Dirty Holding ·{" "}
              {collectTask && roomLabel(collectTask.room_id)}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 mb-2">
            These Supervisor-verified quantities will move from the room to
            Dirty Holding (once):
          </div>
          <table className="vr-table">
            <thead>
              <tr>
                <th>Linen Item</th>
                <th className="num">Verified Qty</th>
              </tr>
            </thead>
            <tbody>
              {(collectTask?.verify_lines || [])
                .filter((l) => Number(l.actual_qty) > 0)
                .map((l) => (
                  <tr key={l.item_id}>
                    <td>{itemName(l.item_id)}</td>
                    <td className="num">{fmtNum(l.actual_qty)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollectTask(null)}>
              Cancel
            </Button>
            <Button
              className="bg-slate-900 text-white hover:bg-slate-800"
              onClick={doCollect}
              disabled={busy}
              data-testid="collect-confirm"
            >
              Confirm Collect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel task (admin only) */}
      <Dialog open={!!cancelTask} onOpenChange={(o) => { if (!o) { setCancelTask(null); setCancelReason(""); } }}>
        <DialogContent data-testid="cancel-task-dialog">
          <DialogHeader>
            <DialogTitle>Cancel checkout task · Room {cancelTask?.room_number ?? (cancelTask && roomLabel(cancelTask.room_id))}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              Cancels this stuck task so a new checkout cycle can start. The task document is kept
              for audit, status becomes <span className="font-mono">cancelled</span>, and the actor
              + reason + timestamp are recorded. <span className="font-semibold">No ledger entries are
              written or reversed.</span>
              {cancelTask?.hk_submitted_at && (
                <div className="mt-1 text-xs">Housekeeper submitted on {cancelTask.hk_submitted_at}
                  {cancelTask?.hk_by_name ? ` by ${cancelTask.hk_by_name}` : ""}.</div>
              )}
              {cancelTask?.verified_at && (
                <div className="mt-1 text-xs">Verified on {cancelTask.verified_at}
                  {cancelTask?.verified_by_name ? ` by ${cancelTask.verified_by_name}` : ""}.</div>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold">Reason (required)</label>
              <textarea
                className="mt-1 w-full border border-slate-200 rounded p-2 text-sm"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. duplicate task from stale status transition; original cycle has no linen to collect"
                data-testid="cancel-task-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelTask(null); setCancelReason(""); }} disabled={busy}>
              Keep task
            </Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={doCancel}
              disabled={busy || !cancelReason.trim()}
              data-testid="cancel-task-confirm"
            >
              {busy ? "Cancelling…" : "Cancel task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View details */}
      <Dialog open={!!detailTask} onOpenChange={() => setDetailTask(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Checkout details · {detailTask && roomLabel(detailTask.room_id)}
            </DialogTitle>
          </DialogHeader>
          {detailTask && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-slate-600">
                <div>
                  Housekeeper: <b>{detailTask.hk_by_name || "—"}</b> ·{" "}
                  {fmtDate(detailTask.hk_submitted_at)}
                </div>
                <div>
                  Verified by: <b>{detailTask.verified_by_name || "—"}</b> ·{" "}
                  {fmtDate(detailTask.verified_at)}
                </div>
                <div>
                  Collected by: <b>{detailTask.collected_by_name || "—"}</b> ·{" "}
                  {fmtDate(detailTask.collected_at)}
                </div>
                <div>Triggered: {fmtDate(detailTask.triggered_at)}</div>
              </div>
              <table className="vr-table">
                <thead>
                  <tr>
                    <th>Linen Item</th>
                    <th className="num">Reported</th>
                    <th className="num">Verified</th>
                    <th className="num">Variance</th>
                    <th>Class</th>
                    <th>Reason</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {(detailTask.verify_lines || detailTask.hk_lines || []).map(
                    (l) => (
                      <tr key={l.item_id}>
                        <td>{itemName(l.item_id)}</td>
                        <td className="num">
                          {fmtNum(l.reported_qty ?? l.qty ?? 0)}
                        </td>
                        <td className="num">
                          {l.actual_qty != null ? fmtNum(l.actual_qty) : "—"}
                        </td>
                        <td className="num">
                          {l.variance != null
                            ? l.variance > 0
                              ? `+${l.variance}`
                              : l.variance
                            : "—"}
                        </td>
                        <td>{l.classification || "—"}</td>
                        <td>{reasonLabel(l.reason)}</td>
                        <td>{l.remarks || "—"}</td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
              {detailTask.has_open_discrepancy && (
                <div className="text-xs text-amber-700">
                  This checkout was collected with an unresolved discrepancy
                  (recorded as a possible loss pending review, not theft).
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailTask(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
